import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';

import { useTranslate } from '../../../i18n/useTranslationModules';
import { CallToAction, ColorKeys, ContentEditors, PageLinkType, SessionTypesEnum, TextOverrides } from '../../../types/working-model';
import { updateHomepageModule } from '../../../store/actions/admin/create-event';
import Icon, { COLORS, ICONS } from '../icon';
import Modal from '../modal/modal';
import { getTextValue, updateTranslateKey, niceDateTimeTz, getDefaultModuleAlignment } from '../../../utils/utils';
import { addChecklistItem } from '../../../store/actions/authentication';
import { ChecklistTitles } from '../../../store/reducers/admin/assistant';
import Controls, { InitialControlsDisplay } from '../../admin/create/session/editor/moduleControls/controls';
import { getStylingOverrides } from '../../live-event/utils';
import { getStartTime } from '../../../utils/get-event-start-time';
import { OptionalComponent } from '../../../utils/optional-component';
import { Countdown } from '../countdown/countdown';
import { EContentColorOverrideNames } from '../edit-section-colors/edit-section-colors-modal';
import CallToActionForm from '../call-to-action/call-to-action';
import EditableTextSlim from '../editable-text/editable-text-slim';
import { Button } from '../button/button';
import { BasicLangingModuleProps } from '../../admin/create/homepage/editor/editor-main';
import { Tooltip } from '../tooltip/tooltip';
import { useTypedSelector } from '../../../store/reducers/use-typed-selector';
import { EUploadOptions, ImageUploader, ImageUploadOptions } from '../image-uploader/image-uploader';
import EditableTextDraftQuill from '../editable-text/editable-text-draft-quill';
import useTranslationRoute from '../../live-event/hooks/use-translation-route';

import '../../../scss/live-event/base/modules/main-event-banner.scss';
import '../../../scss/live-event/base/general-ui/main-event-banner.scss';

// React.forwardRef is used to access refs in children
const EditorMainEventBanner: React.FC<BasicLangingModuleProps> = React.forwardRef<HTMLDivElement, BasicLangingModuleProps>(({ page_module, template, language, baseLanguage }, ref) => {
	const workingEvent = useTypedSelector((state) => state.CreateEventReducer.workingEvent);
	const token = useTypedSelector((state) => state.AuthReducer.token);
	const user = useTypedSelector((state) => state.AuthReducer.user);
	const currentPostRegisterPage = useTypedSelector((state) => state.CreateEventReducer.currentPostRegisterPage);
	const content = page_module?.content;

	const [image, setImage] = useState<{ original: string, edited: string; }>({ original: content?.originalImage ?? "", edited: content?.image ?? "" });
	const [imageModalOpen, setImageModalOpen] = useState<ImageUploadOptions>(null);
	const [callToActionOpen, setCallToActionOpen] = useState(false);
	const [workingCTA, setWorkingCTA] = useState<CallToAction | null>(page_module?.content?.callToAction);

	const dispatch = useDispatch();

	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);

	const layout_type = content?.layout_type || "standard";
	const hasBroadcastEvent = useMemo(() => workingEvent?.sessions?.some(session => session.timestamp), [workingEvent]);

	const now = new Date().getTime();
	const startTime = workingEvent ? (getStartTime(workingEvent, false) as number) : null;
	const startDate = workingEvent && hasBroadcastEvent && startTime ? new Date(startTime) : null;
	const defaultStartTimeText = (startTime ? `${t("homepage:STARTS ON")}  ${niceDateTimeTz(startTime, language)}` : '');
	const _startTimeText = page_module?.content?.start_time ?
		(page_module?.content?.start_time[language] || page_module?.content?.start_time[baseLanguage] || page_module?.content?.start_time?.base || defaultStartTimeText) : defaultStartTimeText;

	const [startsOnText, setStartsOnText] = useState(_startTimeText);

	const styling_overrides = page_module?.styling_overrides;
	//check if the property exists yet
	const countdownIsOn = ("countdownIsOn" in content)
		? content.countdownIsOn
		// backward compatable: check if any session has a broadcast date, then turn on
		: workingEvent?.sessions?.some(session => session.session_type === SessionTypesEnum.broadcast);

	const {
		backgroundColor,
		backgroundImg,
	} = getStylingOverrides(styling_overrides);

	function handleStartsOnText(text: string) {
		setStartsOnText(text);
		dispatch(
			updateHomepageModule({
				...page_module,
				content: {
					...content,
					start_time: updateTranslateKey({
						translateString: page_module?.content?.start_time,
						input: text,
						language,
						baseLanguage,
					}),
				},
			})
		);
	}

	function setHomepageTitle(title: string) {
		dispatch(
			updateHomepageModule({
				...page_module,
				content: {
					...content,
					title: updateTranslateKey({
						translateString: page_module?.content?.title,
						input: title,
						language,
						baseLanguage,
					}),
				},
			})
		);
	}

	const handleDescriptionChange = useCallback(
		(text: string) => {
			if (!token) return;
			dispatch(
				updateHomepageModule({
					...page_module,
					content: {
						...content,
						description: updateTranslateKey({
							translateString: page_module?.content?.description,
							input: text,
							language,
							baseLanguage,
						}),
					},
					is_edited: true,
				})
			);

			// ASSISTANT CHECKLIST ITEM: complete users checklist item for editing event description
			if (user && !user.onboarded.includes(ChecklistTitles.editDescription)) {
				dispatch(addChecklistItem(token, user.id, user.onboarded, ChecklistTitles.editDescription));
			}
		},
		[baseLanguage, content, dispatch, language, page_module, token, user]
	);

	const openImageModal = (type: ImageUploadOptions) => () => {
		setImageModalOpen(type);
	};

	const saveImage = useCallback(
		(image: { original: string; edited: string }) => {
			dispatch(
				updateHomepageModule({
					...page_module,
					content: {
						...page_module.content,
						originalImage: image.original,
						image: image.edited,
					},
				})
			);
			setImage(image);
		},
		[dispatch, page_module]
	);

	function editButton() {
		setCallToActionOpen(true);
	}

	function cancelEditButton() {
		setCallToActionOpen(false);
		setWorkingCTA(null);
	}

	function setCallToActionButton() {
		if (!token) return;
		dispatch(
			updateHomepageModule({
				...page_module,
				content: {
					...content,
					callToAction: workingCTA,
				},
			})
		);

		setCallToActionOpen(false);
		setWorkingCTA(null);

		// ASSISTANT CHECKLIST ITEM: complete users checklist item for editing the button name to Register to Event
		if (user && !user?.onboarded.includes(ChecklistTitles.editButtonName) && workingCTA?.button_name.base === "Register to Event") {
			dispatch(addChecklistItem(token, user.id, user.onboarded, ChecklistTitles.editButtonName));
		}
	}

	function handleStartTimeOverrides(overrides: TextOverrides) {
		const updatedOverrides = {
			...styling_overrides,
			content: {
				...styling_overrides?.content,
				start_time: overrides,
			},
		};
		dispatch(
			updateHomepageModule({
				...page_module,
				styling_overrides: updatedOverrides,
			})
		);
	}

	function handleTitleOverrides(overrides: TextOverrides) {
		const updatedOverrides = {
			...styling_overrides,
			content: {
				...styling_overrides?.content,
				title: overrides,
			},
		};
		dispatch(
			updateHomepageModule({
				...page_module,
				styling_overrides: updatedOverrides,
			})
		);
	}

	function handleDescriptionOverrides(overrides: TextOverrides) {
		const updatedOverrides = {
			...styling_overrides,
			content: {
				...styling_overrides?.content,
				description: overrides,
			},
		};
		dispatch(
			updateHomepageModule({
				...page_module,
				styling_overrides: updatedOverrides,
			})
		);
	}

	const isLinkedToRegistration = content?.callToAction?.content?.page === PageLinkType.registration;
	const callToActionButton = useMemo(() => {
		return (
			<Button classButton={classNames("primary")} template={template}>
				{t(`${namespace}:mainEventBanner.${page_module.id}.callToAction`, t(`$	{namespace}:mainEventBanner.callToAction`, (content?.callToAction?.button_name?.[language] as string) || content?.callToAction?.button_name?.base))}
			</Button>
		);
	}, [content?.callToAction?.button_name, language, template, t, namespace, page_module]);

	const displayBannerControls: InitialControlsDisplay = {
		editLayout: true,
		editColors: true,
		changeSectionPosition: true,
	};

	const { description, title } = getTextValue(content, language);
	return workingEvent && page_module.is_on ? (
		<section ref={ref} className={classNames("editor-wrapper main-event-banner", `main-event-banner-${layout_type}`, backgroundColor, template)}>
			<Controls display={displayBannerControls} openEditModal={() => "leave blank if not editing content"} module={page_module} baseLanguage={baseLanguage} language={language} />
			<div className="module-container" style={backgroundImg}>
				<div className={classNames("banner-title-container", { "post-register": currentPostRegisterPage })}>
					<OptionalComponent display={now < (startTime ? startTime : Infinity)}>
						<EditableTextSlim
							tagType="p"
							value={startsOnText}
							className="evt-caption-1 banner-starts-on"
							onChange={handleStartsOnText}
							colorOverride={styling_overrides?.content?.start_time?.color}
							onOverrideChange={handleStartTimeOverrides}
							defaultColor={ColorKeys.secondaryAccentColor}
							elementName={EContentColorOverrideNames.start_time}
						/>
					</OptionalComponent>

					<EditableTextSlim
						tagType="h1"
						value={t(`${namespace}:mainEventBanner.${page_module.id}.title`, t(`${namespace}:mainEventBanner.title`, title))}
						className="banner-title"
						onChange={setHomepageTitle}
						colorOverride={styling_overrides?.content?.title?.color}
						onOverrideChange={handleTitleOverrides}
						defaultColor={ColorKeys.headingTextColor}
						elementName={EContentColorOverrideNames.title}
					/>

					<EditableTextDraftQuill
						value={t(`${namespace}:mainEventBanner.${page_module.id}.description`, t(`${namespace}:mainEventBanner.description`, description))}
						className="banner-description"
						onChange={handleDescriptionChange}
						styleOverrides={styling_overrides?.content?.description}
						onOverrideChange={handleDescriptionOverrides}
						defaultColor={ColorKeys.headingTextColor}
						elementName={EContentColorOverrideNames.description}
						useNew={workingEvent?.settings?.content_editor === ContentEditors.quill}
						defaultTextAlignment={getDefaultModuleAlignment(page_module, template)}
					/>

					{startDate && countdownIsOn ? <Countdown date={startDate} template={template} /> : null}

					<div className="button-holder" style={!startDate ? { marginTop: 18 } : {}}>
						<div className="call-to-action-wrapper">
							{
								// if post registration and the call to action is linked to registration, show tooltip because the button will be hidden
								currentPostRegisterPage && isLinkedToRegistration ? <Tooltip tooltip="This button will not display when linked to registration">{callToActionButton}</Tooltip> : callToActionButton
							}
							<button className="round edit-button" onClick={editButton}>
								<Icon name={ICONS.EDIT} size={13} color={COLORS.WHITE} />
							</button>
						</div>
						{/* <Button classButton={classNames('watch-video-button secondary', { "no-border": template === TemplateClassNames.O2 })} template={template}>Watch Video</Button> */}
					</div>
				</div>

				<div className="banner-preview-container">
					<div className="header-image-wrapper">
						{image ? (
							<>
								<img src={image.edited} alt="" width="375" height="300" aria-hidden="true" />
								<div className="edit-button-container">
									{content?.originalImage ? (
										<button className="round edit-button" onClick={openImageModal(EUploadOptions.edit)}>
											<Icon name={ICONS.IMAGE_CROP} color={COLORS.WHITE} size={14} />
										</button>
									) : null}
									<button className="round edit-button replace-image" onClick={openImageModal(EUploadOptions.upload)}>
										<Icon name={ICONS.IMAGE} color={COLORS.GRAY} size={16} />
									</button>
								</div>
							</>
						) : (
							<button className="no-style clear no-image" onClick={openImageModal(EUploadOptions.upload)}>
								<Icon name={ICONS.IMAGE} color={COLORS.GRAY} size={16} />
								<span>Upload Image</span>
							</button>
						)}
					</div>
					{/*Leaving this in case we want to add the play button back to main event banner in the future*/}
					{/* <Button classButton={classNames('play-button primary')} template={template}>
						<EIcon name="play" size="medium" />
					</Button> */}
				</div>
			</div>
			<ImageUploader originalImage={image?.original} onSave={saveImage} onClose={() => setImageModalOpen(null)} open={imageModalOpen} />
			<Modal
				cancellable={true}
				closeable={false}
				open={callToActionOpen}
				onRequestClose={cancelEditButton}
				title={"Edit button"}
				footer={
					<>
						<button onClick={cancelEditButton}>Cancel</button>
						<button onClick={setCallToActionButton} className="primary">
							Done
						</button>
					</>
				}
			>
				<CallToActionForm onWorkingCTAChange={setWorkingCTA} workingCTA={workingCTA || page_module?.content?.callToAction} language={language} baseLanguage={baseLanguage} />
			</Modal>
		</section>
	) : null;
});

// because we are wrapping the component in the React.forwardRef()
// we need to explicity say what the display name is
EditorMainEventBanner.displayName = "EditorMainEventBanner";

export default EditorMainEventBanner;
